/* eslint-disable react/jsx-no-bind */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuPortal,
} from "app/components/shared/DropdownMenu";
import Icon from "app/components/shared/Icon";
import { HTMLProps, useCallback, useEffect, useMemo, useState } from "react";
import { useBuildPreferencesStore } from "../lib/useBuildPreferencesStore";
import { BuildView } from "../lib/types/BuildView";
import { useParams } from "react-router-dom";

const Option = ({ children, onChange, checked }: HTMLProps<HTMLInputElement>) => {
  return (
    <label className="flex cursor-pointer items-center gap-1.5">
      <input type="radio" className="peer !m-0" onChange={onChange} checked={checked} />
      <span className="peer-checked:font-medium">{children}</span>
    </label>
  );
};

export function BuildPagePreferences() {
  const { pipeline } = useParams<{ pipeline: string }>();

  const [defaultView, getDefaultView, setDefaultView] = useBuildPreferencesStore((state) => [
    state.defaultView,
    state.getDefaultView,
    state.setDefaultView,
  ]);

  const [updatedAllDefaults, setUpdatedAllDefaults] = useState(false);

  const currentValue = useMemo(() => {
    return getDefaultView(pipeline || "");
  }, [getDefaultView, defaultView, pipeline]);

  const setDefaultForAllPipelines = useCallback(() => {
    setDefaultView(currentValue);
    setUpdatedAllDefaults(true);
  }, [currentValue]);

  // Reset the updatedAllDefaults state if the current value changes
  useEffect(() => {
    if (currentValue !== defaultView) {
      setUpdatedAllDefaults(false);
    }
  }, [currentValue]);

  // Reset the updatedAllDefaults state after 2 seconds
  // to allow the UI to show the confirmation icon briefly.
  useEffect(() => {
    if (updatedAllDefaults) {
      setTimeout(() => {
        setUpdatedAllDefaults(false);
      }, 2000);
    }
  }, [updatedAllDefaults]);

  const isStepsView = useCallback((view: BuildView) => {
    return [BuildView.Canvas, BuildView.Table, BuildView.Waterfall].includes(view);
  }, []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <button
          className="-mr-0.5 flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-purple-100 hover:text-purple-600"
          title="Build page preferences"
        >
          <Icon icon="heroicons/outline/cog-6-tooth" className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent className="p-0" data-testid="build-page-preferences">
          <div className="px-3 py-2">
            <h4>Default view</h4>

            <div className="mt-2 flex flex-col gap-2 text-sm">
              <Option
                checked={currentValue === BuildView.Summary}
                onChange={() => setDefaultView(BuildView.Summary, pipeline)}
              >
                Summary
              </Option>

              <Option
                checked={isStepsView(currentValue)}
                onChange={() =>
                  !isStepsView(currentValue) && setDefaultView(BuildView.Canvas, pipeline)
                }
              >
                Steps
              </Option>

              {isStepsView(currentValue) && (
                <div className="ml-1.5 flex flex-auto justify-between gap-3 border-l border-gray-300 pl-3">
                  <Option
                    checked={currentValue === BuildView.Canvas}
                    onChange={() => setDefaultView(BuildView.Canvas, pipeline)}
                  >
                    Canvas
                  </Option>
                  <Option
                    checked={currentValue === BuildView.Table}
                    onChange={() => setDefaultView(BuildView.Table, pipeline)}
                  >
                    Table
                  </Option>
                  <Option
                    checked={currentValue === BuildView.Waterfall}
                    onChange={() => setDefaultView(BuildView.Waterfall, pipeline)}
                  >
                    Waterfall
                  </Option>
                </div>
              )}

              <Option
                checked={currentValue === BuildView.Tests}
                onChange={() => setDefaultView(BuildView.Tests, pipeline)}
              >
                Tests
              </Option>
              <Option
                checked={currentValue === BuildView.Annotations}
                onChange={() => setDefaultView(BuildView.Annotations, pipeline)}
              >
                Annotations
              </Option>
            </div>
          </div>

          <div className="mb-2 border-t border-gray-300" />

          <div className="px-2 pb-2">
            <button
              className="flex items-center rounded-md border border-slate-400 px-2 py-0.5 text-sm shadow-sm hover:bg-gray-100"
              onClick={setDefaultForAllPipelines}
            >
              Apply to all pipelines
              {updatedAllDefaults && (
                <Icon icon="heroicons/outline/check" className="ml-2 h-4 w-4 text-green-500" />
              )}
            </button>
          </div>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
